'use client';

import { useEffect } from 'react';

/**
 * Reports uncaught errors and rejected promises.
 *
 * React's error boundary only sees failures during render. Anything thrown from
 * an event handler, a timer or an async callback bypasses it entirely — so a
 * report of "it just errors" can have no corresponding server log. This closes
 * that gap.
 */
export function ErrorReporter() {
  useEffect(() => {
    const seen = new Set<string>();

    function report(name: string, message: string, stack: string | undefined) {
      // One report per distinct error per page, so a loop cannot flood the log.
      const key = `${name}:${message}`;
      if (seen.has(key)) return;
      seen.add(key);

      try {
        void fetch('/api/client-error', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, message, stack, url: window.location.href }),
          keepalive: true,
        }).catch(() => {});
      } catch {
        /* never make things worse */
      }
    }

    const onError = (event: ErrorEvent) => {
      report(
        event.error?.name ?? 'Error',
        event.message || String(event.error),
        event.error?.stack ?? `${event.filename}:${event.lineno}:${event.colno}`,
      );
    };

    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      report(
        reason?.name ?? 'UnhandledRejection',
        reason?.message ?? String(reason),
        reason?.stack,
      );
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return null;
}
